import { useState, useEffect, useMemo } from "react";
import { Drawer } from ".";
import CustomButton from '../common/CustomButton';
import SingleSelect from '../common/Search/SingleSelect';
import { HiOutlineMap } from "react-icons/hi";
import { useAzureService } from '../../services';
import axios from '../../utils/axiosConfig';
import { useJwt } from "../../helpers/common";
import StateMappingComponent from "./StateMappingComponent";
import UnsavedChangesIndicator from '../common/UnsavedChangesIndicator';
import toast from "react-hot-toast";

interface ImportProjectModalProps {
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
    onImportSuccess?: () => void;
}

interface ProjectOption {
    value: string;
    label: string;
}

interface EpicOption {
    value: string;
    label: string;
}

const ImportProjectModal = ({ setShowModal, showModal, onImportSuccess }: ImportProjectModalProps) => {
    const JWTdata: any = useJwt();
    const { fetchProjects, fetchEpics, importEpic, updateStages, fetchAzureSettings } = useAzureService();
    const [isLoading, setIsLoading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [epics, setEpics] = useState<EpicOption[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [selectedEpicId, setSelectedEpicId] = useState<string>('');
    
    // State mapping states for Import tab
    const [localMappings, setLocalMappings] = useState<Record<string, any>>({});
    const [jsonInput, setJsonInput] = useState('');
    const [isJsonView, setIsJsonView] = useState(false);
    
    // Separate state for Project States tab
    const [statesMappings, setStatesMappings] = useState<Record<string, any>>({});
    const [statesJsonInput, setStatesJsonInput] = useState('');
    const [isStatesJsonView, setIsStatesJsonView] = useState(false);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [savedStatesMappings, setSavedStatesMappings] = useState<Record<string, any>>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    
    // Add state for display_message from API
    const [displayMessage, setDisplayMessage] = useState<string | null>(null);

    // Add new state to control visibility of the state mapping UI
    const [showStateMappings, setShowStateMappings] = useState(false);
    
    // Add state for the tab selection (Import or Project States)
    const [activeTab, setActiveTab] = useState<'import' | 'states'>('import');

    // Helper function to synchronize JSON with mappings object
    const syncJsonWithMappings = (mappings: Record<string, any>, setJson: (json: string) => void) => {
        setJson(JSON.stringify(mappings, null, 2));
    };

    // Fetch Azure projects
    useEffect(() => {
        if (showModal) {
            getProjects();
            loadAzureSettings();
        }
    }, [showModal]);

    // Fetch epics when a project is selected
    useEffect(() => {
        if (selectedProjectId) {
            // Reset state mappings, epic selection, and hide the mapping UI when project changes
            setShowStateMappings(false);
            setLocalMappings({});
            setJsonInput('');
            setSelectedEpicId(''); // Reset epic selection
            setDisplayMessage(null); // Reset display message
            
            // Find selected project to get the name for API call
            const selectedProject = projects.find(p => p.value === selectedProjectId);
            if (selectedProject) {
                getEpics(selectedProject.label, selectedProjectId);
            }
        } else {
            setEpics([]);
            setSelectedEpicId('');
            setDisplayMessage(null);
        }
    }, [selectedProjectId, projects]);

    // Update JSON when localMappings change (when in UI view) in Import tab
    useEffect(() => {
        if (!isJsonView) {
            syncJsonWithMappings(localMappings, setJsonInput);
        }
    }, [localMappings, isJsonView]);

    // Update JSON when statesMappings change (when in UI view) in States tab
    useEffect(() => {
        if (!isStatesJsonView) {
            syncJsonWithMappings(statesMappings, setStatesJsonInput);
        }
    }, [statesMappings, isStatesJsonView]);

    const getProjects = async () => {
        try {
            setIsLoading(true);
            const projectsList = await fetchProjects();
            
            // Sort projects alphabetically by label
            const sortedProjects = [...projectsList].sort((a, b) => 
                a.label.localeCompare(b.label)
            );
            
            setProjects(sortedProjects);
        } catch (error) {
            // Error is already handled in the service
        } finally {
            setIsLoading(false);
        }
    };

    const getEpics = async (projectName: string, projectId: string) => {
        try {
            setIsLoading(true);
            setEpics([]);
            const { epics: epicsList, statusStages, displayMessage } = await fetchEpics(projectName, projectId);
            
            // Set the display message from API, preserving empty strings
            // This allows the UI to distinguish between null and empty string cases
            // We store the message but only show it once an epic is selected
            setDisplayMessage(displayMessage !== undefined ? displayMessage : '');
            
            // Sort epics alphabetically by label
            const sortedEpics = [...epicsList].sort((a, b) => 
                a.label.localeCompare(b.label)
            );
            
            setEpics(sortedEpics);
            
            // If we have status stages, update the localMappings and JSON for Import tab only
            if (statusStages) {
                setLocalMappings(statusStages);
                syncJsonWithMappings(statusStages, setJsonInput);
            } else {
                // If no status stages were returned, create a default empty structure
                const defaultMapping = {};
                setLocalMappings(defaultMapping);
                syncJsonWithMappings(defaultMapping, setJsonInput);
            }
        } catch (error) {
            // Error is already handled in the service
        } finally {
            setIsLoading(false);
        }
    };

    const handleImport = async () => {
        if (!selectedProjectId || !selectedEpicId) {
            toast.error('Please select both a project and an epic');
            return;
        }

        try {
            setIsImporting(true);
            // Find selected project to get the label for API call
            const selectedProject = projects.find(p => p.value === selectedProjectId);
            
            if (selectedProject) {
                // Parse the current JSON input to get the latest status stages
                let statusStages;
                if (isJsonView) {
                    try {
                        statusStages = JSON.parse(jsonInput);
                    } catch (error) {
                        toast.error('Invalid JSON format. Please fix before importing.');
                        setIsImporting(false);
                        return;
                    }
                } else {
                    statusStages = localMappings;
                }
                
                await importEpic(selectedEpicId, selectedProject.label, statusStages);
                
                if (onImportSuccess) {
                    onImportSuccess();
                }
                handleClose();
            }
        } catch (error) {
            // Error is already handled in the service
        } finally {
            setIsImporting(false);
        }
    };

    const handleSaveStateMappings = async () => {
        try {
            setIsLoadingStates(true);
            
            let statusStages: Record<string, any>;
            
            // Get the current state mappings based on view mode
            if (isStatesJsonView) {
                try {
                    statusStages = JSON.parse(statesJsonInput);
                    
                    // Validate that statusStages is an object
                    if (typeof statusStages !== 'object' || statusStages === null) {
                        throw new Error('Status stages must be a valid object');
                    }
                } catch (error) {
                    console.error('Error parsing JSON:', error);
                    toast.error('Invalid JSON format. Please fix before saving.');
                    setIsLoadingStates(false);
                    return;
                }
            } else {
                statusStages = statesMappings;
            }
            
            // Use the updateStages function from the Azure service
            await updateStages(statusStages);
            
            // Refresh the Azure settings after saving
            await loadAzureSettings();
            
            toast.success('State mappings saved successfully!');
            setSavedStatesMappings(JSON.parse(JSON.stringify(statusStages || {})));
        } catch (error) {
            console.error('Error saving state mappings:', error);
            toast.error('Error saving state mappings');
        } finally {
            setIsLoadingStates(false);
        }
    };

    const handleClose = () => {
        setSelectedProjectId('');
        setSelectedEpicId('');
        setShowStateMappings(false);
        setDisplayMessage(null);
        setActiveTab('import');
        setShowModal(false);
    };

    // Add a new function to load Azure settings using the service
    const loadAzureSettings = async () => {
        try {
            setIsLoadingStates(true);
            const { parsedStages } = await fetchAzureSettings();
            
            // Set the state mappings and JSON input
            setStatesMappings(parsedStages);
            syncJsonWithMappings(parsedStages, setStatesJsonInput);
            setSavedStatesMappings(JSON.parse(JSON.stringify(parsedStages || {})));
        } catch (error) {
            console.error('Error fetching Azure settings:', error);
            toast.error('Error fetching Azure settings');
            setStatesMappings({});
            setStatesJsonInput('{}');
            setSavedStatesMappings({});
        } finally {
            setIsLoadingStates(false);
        }
    };

    // Update the function to handle clicking on Configure State Mappings
    const handleShowStateMappings = () => {
        // Clear the display message when configuring state mappings
        setDisplayMessage(null);
        setShowStateMappings(true);
    };

    // Determine when to show the Configure State Mappings button
    const shouldShowConfigureStateButton = useMemo(() => {
        // Show the button when:
        // 1. Project and epic are selected
        // 2. State mappings are not already shown
        // 3. displayMessage is either null or has actual content (not an empty string)
        return selectedProjectId && 
               selectedEpicId && 
               !showStateMappings && 
               displayMessage !== '';
    }, [selectedProjectId, selectedEpicId, showStateMappings, displayMessage]);

    return (
        <Drawer
            isOpen={showModal}
            onClose={handleClose}
            position="right"
        >
            <div tabIndex={-1} className="h-full bg-white dark:bg-[#111928] flex flex-col">
                <div className="border-b border-gray-200 !p-6 dark:border-gray-700 bg-blue-600 text-white font-semibold">
                    <div className="flex items-center justify-between">
                        <span>{activeTab === 'import' ? 'Azure DevOps Integration' : 'Workflow State Configuration'}</span>
                    </div>
                </div>
                
                {/* Scrollable Content Area - pb-20 adds bottom padding to ensure content isn't hidden behind the fixed buttons */}
                <div className="flex-1 overflow-y-auto pb-20">
                    <div className="p-6">
                        {(isLoading || isImporting || isLoadingStates) && (
                            <div className="absolute inset-0 bg-white dark:bg-[#111928] bg-opacity-70 dark:bg-opacity-70 flex items-center justify-center z-10">
                                <div className="flex flex-col items-center">
                                    <img 
                                        alt="cadence Loader" 
                                        className="w-40 mb-1" 
                                        src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/loader.gif" 
                                    />
                                    <div className="text-center dark:text-white text-base font-normal">
                                        {isImporting ? 'Importing project...' : 
                                         isLoadingStates ? 'Loading state mappings...' : 'Loading data...'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tab Switcher */}
                        <div className="mb-6">
                            <div className="flex items-center justify-center p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                                <button
                                    onClick={() => setActiveTab('import')}
                                    className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                                        activeTab === 'import'
                                            ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200/50 dark:hover:bg-gray-700/50'
                                    }`}
                                >
                                    Import Epic
                                </button>
                                <button
                                    onClick={() => setActiveTab('states')}
                                    className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                                        activeTab === 'states'
                                            ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200/50 dark:hover:bg-gray-700/50'
                                    }`}
                                >
                                    State Mappings
                                </button>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {activeTab === 'import' ? (
                                // Import Tab Content
                                <>
                                    <div>
                                        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                            Choose Your Azure Project
                                            <span className="ml-1">*</span>
                                        </label>
                                        {/* We need to handle disabled state by conditionally rendering */}
                                        {isLoading || isImporting ? (
                                            <div className="w-full p-2.5 text-sm bg-white dark:bg-[#1f2937] text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg cursor-not-allowed">
                                                {selectedProjectId ? projects.find(p => p.value === selectedProjectId)?.label : "Select a project..."}
                                            </div>
                                        ) : (
                                            <SingleSelect
                                                name="project"
                                                options={projects}
                                                placeholder="Select a project..."
                                                onChange={(option) => {
                                                    // The SingleSelect component returns the full option object
                                                    setSelectedProjectId(option?.value || '');
                                                }}
                                                defaultValue={selectedProjectId ? projects.find(p => p.value === selectedProjectId) : undefined}
                                                width="w-full"
                                                className="text-sm"
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                            Choose Epic to Import
                                            <span className="ml-1">*</span>
                                        </label>
                                        {/* We need to handle disabled state by conditionally rendering */}
                                        {isLoading || !selectedProjectId || isImporting ? (
                                            <div className="w-full p-2.5 text-sm bg-white dark:bg-[#1f2937] text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg cursor-not-allowed">
                                                {selectedEpicId ? epics.find(e => e.value === selectedEpicId)?.label : selectedProjectId ? "Select an epic..." : "Select a project first"}
                                            </div>
                                        ) : (
                                            <SingleSelect
                                                name="epic"
                                                options={epics}
                                                placeholder={selectedProjectId ? "Select an epic..." : "Select a project first"}
                                                onChange={(option) => {
                                                    // The SingleSelect component returns the full option object
                                                    setSelectedEpicId(option?.value || '');
                                                }}
                                                defaultValue={selectedEpicId ? epics.find(e => e.value === selectedEpicId) : undefined}
                                                width="w-full"
                                                className="text-sm"
                                            />
                                        )}
                                    </div>

                                    {/* Show Configure State Mappings button only when conditions are met */}
                                    {shouldShowConfigureStateButton && (
                                        <div className="mt-6">
                                            <button
                                                onClick={handleShowStateMappings}
                                                className="w-full py-3 px-4 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-md"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                                </svg>
                                                Configure State Mappings
                                            </button>
                                        </div>
                                    )}

                                    {/* Display the message from API if it exists and is not empty, AND an epic is selected */}
                                    {displayMessage && displayMessage.trim() !== "" && selectedEpicId && (
                                        <div className="mt-4 p-4 border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-600 rounded-lg">
                                            <div className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <p className="text-sm text-yellow-700 dark:text-yellow-300">{displayMessage}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Only show the state mapping component if showStateMappings is true */}
                                    {showStateMappings && (
                                        <StateMappingComponent
                                            localMappings={localMappings}
                                            setLocalMappings={setLocalMappings}
                                            isJsonView={isJsonView}
                                            setIsJsonView={setIsJsonView}
                                            jsonInput={jsonInput}
                                            setJsonInput={setJsonInput}
                                        />
                                    )}
                                </>
                            ) : (
                                // Project States Tab Content - Use data from API
                                <>
                                    <div className="mb-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Map your Azure DevOps workflow states (e.g., New, Active, Resolved) to CadencePro completion percentages. This ensures accurate progress tracking for each team.
                                        </p>
                                    </div>
                                    
                                    {/* Use the data from the API for this tab */}
                                    <StateMappingComponent
                                        localMappings={statesMappings}
                                        setLocalMappings={setStatesMappings}
                                        isJsonView={isStatesJsonView}
                                        setIsJsonView={setIsStatesJsonView}
                                        jsonInput={statesJsonInput}
                                        setJsonInput={setStatesJsonInput}
                                        baselineMappings={savedStatesMappings}
                                        onDirtyChange={setHasUnsavedChanges}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Fixed Button Bar - Different buttons based on active tab */}
                <div className="sticky bottom-0 left-0 right-0 p-6 bg-white dark:bg-[#111928] border-t border-gray-200 dark:border-gray-700 flex justify-between items-center shadow-md z-10">
                    <CustomButton 
                        onClick={handleClose}  
                        variant="cancel"
                        disabled={isImporting || isLoadingStates}
                    >
                        Cancel
                    </CustomButton>
                    
                    {activeTab === 'import' ? (
                        <CustomButton 
                            onClick={handleImport} 
                            disabled={Boolean(
                                isImporting || 
                                isLoading || 
                                !selectedProjectId || 
                                !selectedEpicId || 
                                (displayMessage && displayMessage.trim() !== "" && selectedEpicId && !showStateMappings)
                            )}
                            isLoading={isImporting}
                            className="disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Import Epic
                        </CustomButton>
                    ) : (
                        <div className="flex items-center gap-3">
                            <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} />
                            <CustomButton 
                                onClick={handleSaveStateMappings}
                                variant="primary"
                                className="bg-green-600 hover:bg-green-700"
                                isLoading={isLoadingStates}
                                disabled={isLoadingStates}
                            >
                                Save Mappings
                            </CustomButton>
                        </div>
                    )}
                </div>
            </div>
        </Drawer>
    );
};

export default ImportProjectModal; 